/**
 * FeedbackButton — Always-visible floating "Report Issue / Request Feature"
 * trigger. Mounted in AppShell so every authenticated page has it.
 *
 * Auto-captures: current URL, viewport, user-agent. User picks type +
 * severity + writes a sentence. POSTs to /api/tracker/items.
 */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Bug, Lightbulb, MagicWand, Question, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const TYPE_ICONS = {
  bug: { icon: Bug, label: "Bug — something is broken", color: "text-red-600" },
  feature: { icon: Lightbulb, label: "Feature request — something missing", color: "text-amber-600" },
  improvement: { icon: MagicWand, label: "Improvement — make it better", color: "text-indigo-600" },
  question: { icon: Question, label: "Question — how does this work?", color: "text-zinc-600" },
};

export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const location = useLocation();
  const [form, setForm] = useState({
    title: "", description: "", type: "bug", severity: "normal",
  });
  const [saving, setSaving] = useState(false);

  // Reset form whenever the dialog opens
  useEffect(() => {
    if (open) {
      setForm({ title: "", description: "", type: "bug", severity: "normal" });
      setSubmitted(null);
    }
  }, [open]);

  const submit = async () => {
    if (form.title.trim().length < 4) return toast.error("Title is too short");
    setSaving(true);
    try {
      const r = await api.post("/tracker/items", {
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        severity: form.severity,
        page_url: window.location.pathname + window.location.search,
        user_agent: navigator.userAgent,
      });
      setSubmitted(r.data.item);
      toast.success("Thanks! Logged for the team.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-5 left-5 z-40 h-11 px-4 rounded-full bg-zinc-900 text-white shadow-lg hover:shadow-xl hover:bg-zinc-800 transition flex items-center gap-2 text-sm font-medium"
        data-testid="feedback-fab"
        aria-label="Report an issue or request a feature"
      >
        <Bug size={16} weight="bold" /> Report
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="feedback-dialog">
          {submitted ? (
            <div className="text-center py-6">
              <CheckCircle size={42} weight="duotone" className="mx-auto text-emerald-600" />
              <div className="mt-3 font-semibold text-zinc-900">Logged!</div>
              <div className="text-sm text-zinc-500 mt-1">
                Tracking ID: <span className="font-mono text-xs">{submitted.id.slice(0, 8)}</span>
              </div>
              <div className="text-xs text-zinc-400 mt-2">You can see status updates under Settings → Tracker.</div>
              <Button className="mt-4" onClick={() => setOpen(false)}>Close</Button>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Tell us what&apos;s up</DialogTitle>
                <DialogDescription>
                  Reporting from <span className="font-mono text-xs text-zinc-700">{location.pathname}</span>.
                  We auto-attach your role, browser, and the page URL.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1">
                    {Object.entries(TYPE_ICONS).map(([k, { icon: Icon, label, color }]) => (
                      <button
                        key={k}
                        onClick={() => setForm({ ...form, type: k })}
                        className={`flex items-start gap-2 text-left p-2 rounded border transition ${form.type === k ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`}
                        data-testid={`type-${k}`}
                      >
                        <Icon size={18} weight="duotone" className={color} />
                        <span className="text-xs leading-tight">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-xs">One-line summary</Label>
                  <Input
                    placeholder="e.g. Cannot save payroll run for June"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="feedback-title"
                  />
                </div>

                <div>
                  <Label className="text-xs">Details (optional)</Label>
                  <Textarea
                    rows={4}
                    placeholder="Steps to reproduce, expected vs actual behaviour, screenshots…"
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    data-testid="feedback-description"
                  />
                </div>

                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select value={form.severity} onValueChange={(v) => setForm({ ...form, severity: v })}>
                    <SelectTrigger data-testid="feedback-severity"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blocker">🚨 Blocker — work is stopped</SelectItem>
                      <SelectItem value="high">🔴 High — major friction</SelectItem>
                      <SelectItem value="normal">🟡 Normal</SelectItem>
                      <SelectItem value="low">⚪ Low — nice-to-have</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={submit} disabled={saving} data-testid="submit-feedback-btn">
                  {saving ? "Sending…" : "Submit"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
